import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { chargerTransactionActivityUrl } from '../../react-query/endpoints';
import { useEffect } from 'react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ReferenceLine } from 'recharts';
import {  Card, CircularProgress, Container, Grid, IconButton, Paper, Stack, Typography, styled, useMediaQuery, useTheme, Box, Button, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import { differenceInSeconds, format, formatDuration, intervalToDuration, isValid } from 'date-fns';
import { is } from '../../util/is';
import { convertUTCToLocal, convertUTCToLocalTime, convertUTCToLocalDateTime, getCurrentLocalTime } from '../../util/timezoneUtils';
import { Icon } from '@iconify/react';
import ChargingFill from "@iconify/icons-eva/charging-outline";
import AlertFill from "@iconify/icons-eva/alert-triangle-fill";
import ClockIcon from "@iconify/icons-eva/clock-outline";
import FlashIcon from "@iconify/icons-eva/flash-outline";
import CarIcon from "@iconify/icons-eva/car-outline";
import CreditCardIcon from "@iconify/icons-eva/credit-card-outline";
import Page from '../Page';
import { MuiSnackBar } from '../@material-extend';
import ChargemilesLogo from "../../image/networkFilter/Chargemiles.png";
import MoveonEVLogo from "../../image/networkFilter/MoveonEV.png";
import AmikLogo from "../../image/networkFilter/AmikCharge.png";
import TechvoltLogo from "../../image/networkFilter/techvoltNetwork.png";
import ACSLogo from "../../image/networkFilter/acsICON.png";
import ZFPLogo from "../../image/networkFilter/zeroFootPrint.png";


//------------------------------------------------CONSTANTS------------------------------

const APP_BLUE = "#1976D2";
const STATUS_ARR = [
  { status: "Preparing", message: "Plug in the charging cable to your vehicle.", variant: "error" },
  { status: "Charging", message: "Charging in progress.", variant: "success" },
  { status: "Available", message: "Connection in progress.", variant: "success" },
  { status: "SuspendedEV", message: "Charging stopped by EV.", variant: "error" },
  { status: "SuspendedEVSE", message: "Plug in the charging cable to your vehicle.", variant: "error" },
  { status: "Finishing", message: " Finishing the charger session", variant: "success" },
  { status: "Faulted", message: " A Fault occurred", variant: "error" },
]

const CURRENCY_ENUM = {
    CAD:"C$",
    INR:"₹"
}
//------------------------------------------------------------------------------------------


//-----------------------------------------------STYLED AND HELPER COMPONENTS----------------------------------------------------------------------

export const StyledTypography = styled(Typography)(({ theme, color, fontSize, fontWeight, backgroundColor }) => ({
  color: color ? color : "black",
  fontSize: fontSize ? fontSize : theme.spacing(2.3),
  fontWeight: fontWeight ? fontWeight : "500",
  backgroundColor: backgroundColor ? backgroundColor : "#ffff"
}));

export const StyledCard = styled(Card)(({ theme, backgroundColor }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: theme.spacing(2.5),
  backgroundColor: backgroundColor ? backgroundColor : "#ffffff",
  borderRadius: "16px",
  border: "1px solid rgba(145, 158, 171, 0.12)",
  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    boxShadow: "0 6px 24px 0 rgba(0,0,0,0.08)",
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    minHeight: "80px"
  }
}));

const pulseRing = keyframes`
  0% { transform: scale(0.8); box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.7); }
  70% { transform: scale(1); box-shadow: 0 0 0 10px rgba(25, 118, 210, 0); }
  100% { transform: scale(0.8); box-shadow: 0 0 0 0 rgba(25, 118, 210, 0); }
`;

const pulseRingWarning = keyframes`
  0% { transform: scale(0.8); box-shadow: 0 0 0 0 rgba(255, 171, 0, 0.7); }
  70% { transform: scale(1); box-shadow: 0 0 0 10px rgba(255, 171, 0, 0); }
  100% { transform: scale(0.8); box-shadow: 0 0 0 0 rgba(255, 171, 0, 0); }
`;

const PulseDot = styled(Box)(({ color, iswarning }) => ({
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  backgroundColor: color || APP_BLUE,
  animation: `${iswarning === 'true' ? pulseRingWarning : pulseRing} 2s infinite`,
  display: 'inline-block',
}));



// Custom tooltip for Recharts energy chart
const EnergyTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const time = new Date(label);
    const timeStr = time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    return (
      <Box sx={{ bgcolor: 'rgba(33, 43, 54, 0.92)', color: '#fff', px: 1.5, py: 1, borderRadius: '8px', fontSize: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
        <Box sx={{ fontWeight: 600, mb: 0.3 }}>{timeStr}</Box>
        <Box sx={{ color: 'rgba(255,255,255,0.8)' }}>{Number(payload[0].value).toFixed(3)} kWh</Box>
      </Box>
    );
  }
  return null;
};

export const ErrorComponent = ({message1 ,message2,showRefreshBtn,action})=>{
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  return(
    <Page
    title="Transaction"
    sx={{ marginTop: { xs: "128px", md: "62px", overflow: "hidden" } }}
  >
    <Container
      sx={{
      margin: "auto",
      }}
      style={{
        width: '100%',
        padding: isSmallScreen ? theme.spacing(2) : theme.spacing(14),
      }}
      maxWidth="lg"
    >
      <StyledCard backgroundColor="#FDEDED">
        <Icon icon={AlertFill} color="#f31111" fontSize={isSmallScreen ? theme.spacing(3.5) : theme.spacing(7)} />
        <StyledTypography backgroundColor="#FDEDED" fontSize={isSmallScreen ? theme.spacing(2) : theme.spacing(4)} fontWeight="800" color="red"> {message1} — <strong>{message2}!!</strong> </StyledTypography>
      </StyledCard>
      {showRefreshBtn && action && <IconButton onClick={() => action()}
                  disableFocusRipple={true}
                  disableRipple={true}
                    sx={{ marginRight: isSmallScreen ? 0.25 : 1, marginTop:theme.spacing(1)}}>
                    <Icon icon="eva:refresh-outline" width={isSmallScreen ? 48 : 64} height={isSmallScreen ? 48 : 64} color="#48C13E" />
                  </IconButton>}
    </Container>
  </Page>
  )
}

//------------------------------------------------------------------------------------------

//------------------------------------------DATE TIME RELATED HELPER FUNCTIONS------------------------------------------------

export const onlyTime = (date) => {
  return convertUTCToLocalTime(date);
};

export const currentDate = () => {
  return getCurrentLocalTime();
};


export const formattedTime = (end, start) => {
  const differenceInSecs = differenceInSeconds(end, start);

  const duration = intervalToDuration({ start: 0, end: differenceInSecs * 1000 });
  const formattedDuration = formatDuration(duration, {
    format: ["minutes", "seconds"],
  });
  const updatedFormattedDuration = formattedDuration.replace("minutes", "mins");
  const finalFormattedDuration = updatedFormattedDuration.replace("seconds", "secs");
  return finalFormattedDuration;
};

// Enhanced duration calculation for active charging sessions
export const calculateChargingDuration = (startTime, stopTime = null) => {
  // console.log(`[Charging Duration] Calculating duration - Start: ${startTime}, Stop: ${stopTime}`);
  
  // Parse UTC timestamp properly
  let start;
  if (typeof startTime === 'string') {
    // Check if already has timezone info (Z, +, or -)
    // Only consider it has timezone if it's in ISO format with timezone
    const hasTimezone = startTime.includes('Z') || 
                        startTime.includes('+') || 
                        startTime.match(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}-\d{2}:\d{2}/);
    
    if (hasTimezone) {
      // console.log(`[Charging Duration] Timestamp already has timezone info: ${startTime}`);
      start = new Date(startTime);
    } else if (startTime.includes(' ')) {
      // Handle format like "2025-10-21 15:53:57.111000" - treat as UTC
      const [datePart, timePart] = startTime.split(' ');
      const [year, month, day] = datePart.split('-');
      const [hours, minutes, seconds] = timePart.split(':');
      
      // console.log(`[Charging Duration] Parsing UTC components - Year: ${year}, Month: ${month}, Day: ${day}, Hours: ${hours}, Minutes: ${minutes}, Seconds: ${seconds}`);
      
      // Create UTC date object explicitly
      start = new Date(Date.UTC(
        parseInt(year),
        parseInt(month) - 1, // Month is 0-indexed
        parseInt(day),
        parseInt(hours),
        parseInt(minutes),
        parseInt(seconds || 0)
      ));
    } else {
      // Standard ISO format or other
      start = new Date(startTime);
    }
  } else {
    start = new Date(startTime);
  }
  
  const end = stopTime ? new Date(stopTime) : new Date();
  
  // console.log(`[Charging Duration] Parsed dates - Start: ${start.toISOString()}, End: ${end.toISOString()}`);
  // console.log(`[Charging Duration] Start UTC: ${start.toUTCString()}, End UTC: ${end.toUTCString()}`);
  
  // Ensure we're working with valid dates
  if (!isValid(start) || !isValid(end)) {
    console.warn('[Charging Duration] Invalid dates detected:', { start, end });
    return "0 mins 0 secs";
  }
  
  const differenceInSecs = differenceInSeconds(end, start);
  // console.log(`[Charging Duration] Difference in seconds: ${differenceInSecs}`);
  
  // If the difference is negative (shouldn't happen), return 0
  if (differenceInSecs < 0) {
    console.warn('[Charging Duration] Negative duration detected, returning 0');
    return "0 mins 0 secs";
  }
  
  const duration = intervalToDuration({ start: 0, end: differenceInSecs * 1000 });
  const formattedDuration = formatDuration(duration, {
    format: ["minutes", "seconds"],
  });
  const updatedFormattedDuration = formattedDuration.replace("minutes", "mins");
  const finalFormattedDuration = updatedFormattedDuration.replace("seconds", "secs");
  
  // console.log(`[Charging Duration] Final duration: ${finalFormattedDuration}`);
  return finalFormattedDuration;
};

//------------------------------------------------------------------------------------------

// Shared calculation: exact formula for max limits from amount + connector pricing
export const calculateMaxLimits = (amount, connector) => {
  if (!connector || amount <= 0) return { maxKwh: 0, maxMins: 0 };
  const {
    fixedFeeCharging = 0,
    fixedFeeStarting = 0,
    priceperKW = 0,
    pricePerKw = 0,
    priceperMin = 0,
    pricePerMin = 0,
    taxPercent = 0,
    transactionPercent = 0,
    pgChargesBearer,
  } = connector;

  const priceKW = Number(priceperKW) || Number(pricePerKw) || 0;
  const priceMin = Number(priceperMin) || Number(pricePerMin) || 0;

  if (priceKW <= 0 && priceMin <= 0) return { maxKwh: 0, maxMins: 0 };

  // When pgChargesBearer is CPO, the operator absorbs the gateway fee
  // so transactionPercent should be 0 (matching backend logic in getAuthChargerAndTransactionDetails)
  const effectiveTransactionPercent = pgChargesBearer === "CPO" ? 0 : Number(transactionPercent);

  const transactionFeesGSTTax = effectiveTransactionPercent / 100;
  const transactionFeesAndAdditionTax = transactionFeesGSTTax * Number(amount);
  const subtractAdditionTax = Number(amount) - transactionFeesAndAdditionTax;
  const gstTaxcalculate = 1 + Number(taxPercent) / 100;
  const applyGstTax = subtractAdditionTax / gstTaxcalculate;
  const subtractOtherCharges = applyGstTax - Number(fixedFeeCharging) - Number(fixedFeeStarting);

  let maxKwh = 0;
  if (priceKW > 0) {
    const totalKw = subtractOtherCharges / priceKW;
    maxKwh = totalKw > 0 ? Number(totalKw.toFixed(3)) : 0;
  }

  let maxMins = 0;
  if (priceMin > 0) {
    const totalMin = subtractOtherCharges / priceMin;
    maxMins = totalMin > 0 ? Number(totalMin.toFixed(2)) : 0;
  }

  return { maxKwh, maxMins };
};


//------------------------------------------------API CALL FUNCTION---------------------------------------------

const fetchTransactionRelatedDetails = async (endPoint, activitytoken) => {
  const response = await axios.get(chargerTransactionActivityUrl(endPoint), {
    headers: {
      authorization:`Bearer ${activitytoken}`
    }
  })
  return response.data;
}

const stopTransactionRequest = async (endPoint,activitytoken)=>{
  const response = await axios.post(chargerTransactionActivityUrl(endPoint),{},{
    headers: {
      authorization:`Bearer ${activitytoken}`
    }
  })
  return response.data;

}

const ChargerTransactionStats = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmallerScreen = useMediaQuery('(max-width: 280px) and (max-height: 653px)');

  // Retrieve specific query parameters
  const token = queryParams.get('token');

  //  STATES---------------------------------------------------------------
  const [isInvalidToken,setIsInvalidToken] = useState(false);
  const [firstTimeFetchingDone, setFirstTimeFetchingDone] = useState(false)
  const [refetchIntervalVal, setRefetchIntervalVal] = useState(false);
  const [graphData, setGraphData] = useState([]);
  const [statisticCards, setStatisticsCards] = useState([
    { label: "Charging Duration", value: "", title: "chargingDuration", prefix: "", suffix: "" },
    { label: "Energy Consumed", value: "", title: "energyConsumed", prefix: "", suffix: "kWh" },
    { label: "Start Meter Reading", value: "", title: "start_meter_value", prefix: "", suffix: "" },
    { label: "Amount Paid", value: "", title: "totalAmountPaid", prefix: "", suffix: "" },
  ])
  const [transactionDetails, setTransactionDetails] = useState({});
  const [connectorStat, setConnectorStat] = useState({});
  const [stopRefetching, setStopRefetching] = useState(false);
  const [retryCounter, setRetryCounter] = useState(0);
  const currentDateTime = currentDate();
  const [messageOption, setMessageOption] = useState({
    open: false,
    message: '',
    variant: 'success'
  });
  const [showChargingFailedError, setShowCharingFailedError] = useState(false);
  const [isTransactionCompleted, setIsTransactionCompleted] = useState(false);
  const [clickedStop, setClickedStop] = useState(false);
  const [chargerLogo, setChargerLogo] = useState();

  //---------------------------------------------------------------------------------------------------

  //-------------------------------------METHODS----------------------------------------------------------------------------

  const handleReload = () => {
    window.location.reload();
  };

  const onFetchingTransactionDetails = (data) => {
    const { measurandValues, transactions } = data;
    if (!is.empty(transactions) && !is.empty(transactions[0])) {
      const { starttime, stoptime, start_meter_value, current_meter_value, currency,energyUnit } = transactions[0]
      if (!is.nullOrUndefined(stoptime)) setStopRefetching(true);
      // Parse UTC timestamps correctly to avoid timezone issues
      const parseUTC = (t) => {
        if (is.nullOrUndefined(t)) return null;
        let tStr = t;
        if (typeof tStr === 'string' && !tStr.includes('Z') && !tStr.includes('+')) {
          tStr = tStr.replace(' ', 'T') + 'Z';
        }
        return new Date(tStr);
      };

      const startTime = parseUTC(starttime) || new Date();
      const stopTime = parseUTC(stoptime) || new Date();
      const energyUnitLowerCase = String(energyUnit).toLowerCase()
      const chargingDuration = calculateChargingDuration(starttime, stoptime)
      const startMeterVal = !is.nullOrUndefined(start_meter_value) ? Number(start_meter_value) : 0;
      const currentMeterVal = !is.nullOrUndefined(current_meter_value) ? Number(current_meter_value) : 0;
      const currentEnergyConsumption = currentMeterVal > startMeterVal  ?  currentMeterVal - startMeterVal : 0;

          let energyConsumedToShow=0;
          let energyUnitToShow="";
      if(energyUnitLowerCase==="wh"){
        const energyInKWh =  (Number(currentEnergyConsumption) / 1000).toFixed(2);
        if(energyInKWh<0.5){
          energyConsumedToShow = Number(currentEnergyConsumption);
          energyUnitToShow = "Wh";

        }else{
          energyConsumedToShow = energyInKWh;
          energyUnitToShow = "kWh";
        }
      }else{
        const energyInKWh =  Number(currentEnergyConsumption)
        if(energyInKWh<0.5){
          energyConsumedToShow = energyInKWh *1000;
          energyUnitToShow = "Wh";

        }else{
          energyConsumedToShow = energyInKWh;
          energyUnitToShow = "kWh";
        }
      }
      // Calculate dynamic charge percentage using the same formula as PaymentForm
      let dynamicChargePercent = 0;
      const amountPaid = Number(transactions[0].totalAmountPaid) || 0;
      const pricePerKw = Number(transactions[0].pricePerKw) || 0;
      const pricePerMin = Number(transactions[0].pricePerMin) || 0;
      
      if (amountPaid > 0) {
        if (pricePerKw > 0) {
          // Use the exact formula for target kWh (accounts for tax/fees)
          const limits = calculateMaxLimits(amountPaid, transactions[0]);
          const targetKwh = limits.maxKwh;
          let currentKwh = 0;
          if (energyUnitLowerCase === "wh") {
            currentKwh = Number(currentEnergyConsumption) / 1000;
          } else {
            currentKwh = Number(currentEnergyConsumption);
          }
          if (targetKwh > 0) {
            dynamicChargePercent = Math.min(100, Math.round((currentKwh / targetKwh) * 100));
          }
        } else if (pricePerMin > 0) {
          const limits = calculateMaxLimits(amountPaid, transactions[0]);
          const targetMins = limits.maxMins;
          const diffSecs = differenceInSeconds(stopTime, startTime);
          const currentMins = diffSecs / 60;
          if (targetMins > 0) {
            dynamicChargePercent = Math.min(100, Math.round((currentMins / targetMins) * 100));
          }
        }
      } else {
        dynamicChargePercent = !is.nullOrUndefined(transactions[0].batteryPercent) ? Number(transactions[0].batteryPercent) : 0;
      }

      const finalTransactionDetails = { ...transactions[0], chargingDuration, energyConsumed:energyConsumedToShow,suffix:energyUnitToShow, dynamicChargePercent }

      // Build cumulative kWh graph data point for this poll
      let currentKwhForGraph = 0;
      if (energyUnitLowerCase === "wh") {
        currentKwhForGraph = Number(currentEnergyConsumption) / 1000;
      } else {
        currentKwhForGraph = Number(currentEnergyConsumption);
      }
      // Append new data point with current timestamp and cumulative kWh
      setGraphData(prev => {
        const newPoint = { time: Date.now(), kwh: Number(currentKwhForGraph.toFixed(3)) };
        // Ensure we start from origin (0,0) if this is the first real point
        if (prev.length === 0) {
          return [
            { time: startTime.getTime(), kwh: 0 },
            newPoint
          ];
        }
        return [...prev, newPoint];
      });

      const updatedStatisticCards = statisticCards.map(({ title, value, label, prefix, suffix }) => ({ title, label, value: finalTransactionDetails[title], prefix:title==="totalAmountPaid"?CURRENCY_ENUM[currency]:prefix, suffix:title==="energyConsumed"?finalTransactionDetails["suffix"]:suffix }));
      setTransactionDetails(finalTransactionDetails);
      setStatisticsCards(updatedStatisticCards);

      if (!is.nullOrUndefined(stoptime)) {
        setIsTransactionCompleted(true);
        setConnectorStat((connectorStat) => ({ ...connectorStat, status: "Completed" }))
        setMessageOption({
          ...messageOption,
          open: true,
          message: "Transaction Succesfully Completed",
          variant: "success"
        })
      }
    }
    if (!is.empty(data) && !is.empty(measurandValues)) {
      // measurandValues are used for other purposes if needed
      // Graph data is now built from cumulative energy in the transaction handler above
    }
  }

  const { data: connectorData, isLoading: isLoadingConnectorStatus,refetch:refetchConnectorStatus } = useQuery(["connector"], () => fetchTransactionRelatedDetails("connector", token), {
    enabled: !is.empty(token),
    refetchInterval: refetchIntervalVal,
    onSuccess: async (data) => {
      if (is.empty(data)) {
        return
      }
      const { connectorStatus } = data;
      if (is.empty(connectorStatus) && is.empty(connectorStatus[0])) {
        return
      }
      const connStatusDetails = connectorStatus[0];
      const provider = connStatusDetails.provider;
      if (provider === "MoveonEV") {
        setChargerLogo(MoveonEVLogo);
      } else if (provider === "ChargeMiles") {
        setChargerLogo(ChargemilesLogo);
      } else if (provider === "Techvolt Network") {
        setChargerLogo(TechvoltLogo);
      } else if (provider === "ACS Energy") {
        setChargerLogo(ACSLogo);
      } else if (provider === "ZeroFootPrint") {
        setChargerLogo(ZFPLogo);
      } else {
        setChargerLogo(AmikLogo);
      }
      const messageToShow = STATUS_ARR.find(({ status }) => status.toLowerCase() === connStatusDetails.status.toLowerCase())
      let updatedMessageOp
      if (messageToShow) {
        updatedMessageOp = { ...messageToShow, open: true };
        setMessageOption(updatedMessageOp)
      }
      setConnectorStat({ ...connectorStatus[0] })
      if (updatedMessageOp.status !== "Charging") {
        if (retryCounter <= 15) setRetryCounter(oldC => oldC + 1);
        else {
          setRefetchIntervalVal(false)
          setShowCharingFailedError(true)
          return
        };
        setRefetchIntervalVal(10000)
        return
      };
      try {
        setFirstTimeFetchingDone(true)
        setRetryCounter(0)
        setRefetchIntervalVal(10000)

      } catch (error) {
        console.error(error)
      }
    },
    onError: (error) => {
      console.log(error)
      const {response} = error;
      if(!is.empty(response) && !is.empty(response.data) && !response?.data?.auth && (response?.data?.message==="Failed to authenticate token." ||  response?.data?.message==="Invalid Request")){
       setIsInvalidToken(true);
       return;
      }
      if (retryCounter <= 15) setRetryCounter(oldC => oldC + 1);
      else {
        setRefetchIntervalVal(false)
        setShowCharingFailedError(true)
        return
      };
      setRefetchIntervalVal(10000)
      return
    }
  });

  const { isLoading: isFetchingTransactionDetails, isStale, refetch, isRefetching } = useQuery(["activityDetails"], () => fetchTransactionRelatedDetails("activitydetails", token), {
    enabled: firstTimeFetchingDone && !isTransactionCompleted,
    staleTime: 20000,
    refetchInterval: 10000,
    onSuccess: onFetchingTransactionDetails,
    onError: (err) => {
      setMessageOption({
        ...messageOption,
        open: true,
        message: err.message,
        variant: "error"
      })
    }
  })

  const  { mutate } = useMutation(()=> stopTransactionRequest("stoptransaction",token),{
    onSuccess:()=>refetch(),
    onError:(error)=>{
      setMessageOption({
        ...messageOption,
        open: true,
        message: error?.response?.data?.message ?? error?.message,
        variant: "error"
      })
     
    },
    onSettled:()=> setClickedStop(false)
  })

  useEffect(()=>{
    if(clickedStop){
      mutate()
    }

  },[clickedStop])

  useEffect(() => {
    if (isStale && firstTimeFetchingDone && !stopRefetching) {
      refetch()
    }
  }, [isStale, firstTimeFetchingDone, stopRefetching, refetch]);

  useEffect(()=>{
    let timer;
   if(isTransactionCompleted){
     timer = setTimeout(() => {
       navigate(`/invoice?token=${token}`);
    }, 10000);


    return ()=>{
      clearTimeout(timer)
    }
   }
  },[isTransactionCompleted,token,navigate])
 
  // console.log(token)
  // console.log(graphData)
  // console.log(statisticCards)
  
  if (is.empty(token) || (isInvalidToken)) {
    return <ErrorComponent message1={"Invalid Request"} message2={"Please try again with valid information"}/>
  };

  if (isLoadingConnectorStatus) {
    return <div style={{ margin: 300 }}><CircularProgress
      color="success"
      size={40}
      thickness={4}
      variant="indeterminate" /> </div>
  };
  
  if(showChargingFailedError) {
    return(
      <Stack>

        <ErrorComponent message1={"Charging status rejected by charger"} message2={"Please try again by clicking refresh"} showRefreshBtn={true} action={handleReload} />
        
      </Stack>
      )
  };

  return (
    <Page
      title="Transaction"
      sx={{ marginTop: { xs: "128px", md: "62px", overflow: "hidden" } }}
    >
      <Container
        sx={{
          margin: "auto",
        }}
        style={{
          width: '100%',
          padding: isSmallScreen ? theme.spacing(0) : theme.spacing(4),
        }}
        maxWidth="lg"
      >
        {!showChargingFailedError && <Paper elevation={0} sx={{ padding: isSmallScreen ? 2 : 4, borderRadius: '24px', border: '1px solid rgba(145, 158, 171, 0.12)', boxShadow: '0 8px 32px 0 rgba(0,0,0,0.04)' }} >
          <Stack spacing={4}>
            <Box sx={{
              background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(0, 0, 0, 0) 100%)',
              borderRadius: '20px',
              p: isSmallScreen ? 2 : 3,
            }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{ p: 1, bgcolor: 'white', borderRadius: '12px', boxShadow: '0 4px 12px 0 rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', width: isSmallScreen ? 48 : 80, height: isSmallScreen ? 48 : 80 }}>
                  <img src={chargerLogo} alt="chargemilesLogo" style={{ objectFit: 'contain', width: '100%', height: '100%' }} />
                </Box>
                <Box flexGrow={1}>
                  <StyledTypography fontSize={isSmallScreen ? theme.spacing(2.2) : theme.spacing(3.5)} fontWeight="800" sx={{ background: 'transparent' }}>
                    {connectorStat.chargepoint_name}
                  </StyledTypography>
                  <StyledTypography color="text.secondary" fontSize={isSmallScreen ? theme.spacing(1.2) : theme.spacing(1.8)} sx={{ background: 'transparent', mt: 0.5 }}>
                    {connectorStat.addressStreet}
                  </StyledTypography>
                </Box>
              </Stack>
              <Grid container justifyContent="space-between" alignItems="center" sx={{ mt: 3, pt: 3, borderTop: '1px dashed rgba(145, 158, 171, 0.24)' }}>
                <Grid item xs={12} sm={12} lg={6} md={6}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <PulseDot iswarning={["CHARGING", "SUSPENDEDEV", "SUSPENDEDEVSE", "FINISHING", "IN USE"].includes(connectorStat.status?.toUpperCase()) ? 'true' : 'false'} color={["CHARGING", "SUSPENDEDEV", "SUSPENDEDEVSE", "FINISHING", "IN USE"].includes(connectorStat.status?.toUpperCase()) ? theme.palette.warning.main : messageOption.variant !== "error" ? APP_BLUE : theme.palette.error.main} />
                    <StyledTypography fontWeight="700" fontSize={isSmallScreen ? theme.spacing(1.8) : theme.spacing(2)} sx={{
                      color: ["CHARGING", "SUSPENDEDEV", "SUSPENDEDEVSE", "FINISHING", "IN USE"].includes(connectorStat.status?.toUpperCase()) ? theme.palette.warning.dark : messageOption.variant !== "error" ? theme.palette.primary.dark : theme.palette.error.dark,
                      backgroundColor: ["CHARGING", "SUSPENDEDEV", "SUSPENDEDEVSE", "FINISHING", "IN USE"].includes(connectorStat.status?.toUpperCase()) ? 'rgba(255, 171, 0, 0.16)' : messageOption.variant !== "error" ? 'rgba(25, 118, 210, 0.16)' : 'rgba(255, 72, 66, 0.16)',
                      padding: '6px 16px',
                      borderRadius: '8px',
                      textTransform: 'uppercase',
                      letterSpacing: 1,
                      background: 'transparent'
                    }}>
                      {connectorStat.status}
                    </StyledTypography>
                  </Box>
                </Grid>
                {!isTransactionCompleted && <Grid item xs={12} sm={12} lg={6} md={6} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, mt: { xs: 2, md: 0 }, gap: 1.5 }}>
                  <Button
                    variant="contained"
                    onClick={() => refetch()}
                    startIcon={<Icon icon="eva:refresh-outline" width={24} height={24} />}
                    sx={{
                      px: 3, 
                      py: 1, 
                      borderRadius: '10px', 
                      boxShadow: '0 8px 16px 0 rgba(25, 118, 210, 0.24)', 
                      background: "linear-gradient(135deg, #1976D2 0%, #42A5F5 100%)", 
                      color: "white",
                      fontWeight: 700,
                      "&:hover": { 
                        background: "linear-gradient(135deg, #1565C0 0%, #1976D2 100%)", 
                        boxShadow: "0 6px 20px 0 rgba(25, 118, 210, 0.5)" 
                      } 
                    }}
                  >
                    Refresh
                  </Button>
                  <Button 
                    variant="contained" 
                    onClick={() => mutate()}
                    disabled={clickedStop}
                    startIcon={<Icon icon="eva:stop-circle-outline" width={24} />}
                    sx={{
                      background: "linear-gradient(135deg, #d32f2f 0%, #f44336 100%)",
                      boxShadow: "0 4px 14px 0 rgba(211, 47, 47, 0.39)",
                      color: "white",
                      fontWeight: 700,
                      borderRadius: '10px',
                      px: 3,
                      py: 1,
                      "&:hover": {
                        background: "linear-gradient(135deg, #b71c1c 0%, #d32f2f 100%)",
                        boxShadow: "0 6px 20px 0 rgba(211, 47, 47, 0.5)",
                      }
                    }}
                  >
                    Stop
                  </Button>
                </Grid>}
              </Grid>
            </Box>
            {
              <>
                <Grid container spacing={2}>
                  {
                    !statisticCards.find((card) => (card.value === "")) && statisticCards.map(({ label, value, prefix, suffix, title }, index) => {
                      let itemIcon = ClockIcon;
                      let itemColor = theme.palette.info.main;
                      let itemBg = alpha(theme.palette.info.main, 0.12);
                      
                      if (title === 'energyConsumed') {
                        itemIcon = FlashIcon;
                        itemColor = theme.palette.warning.main;
                        itemBg = alpha(theme.palette.warning.main, 0.12);
                      } else if (title === 'totalAmountPaid') {
                        itemIcon = CreditCardIcon;
                        itemColor = theme.palette.primary.main;
                        itemBg = alpha(theme.palette.primary.main, 0.12);
                      } else if (title === 'start_meter_value') {
                        itemIcon = "mdi:speedometer";
                        itemColor = theme.palette.primary.main;
                        itemBg = alpha(theme.palette.primary.main, 0.12);
                      }

                      return (
                      <Grid item md={6} lg={6} xs={12} sm={6} key={`${label}-${index}-${label}`}>
                        <StyledCard sx={{ minHeight: isSmallerScreen ? "90px" : "120px", alignItems: 'flex-start', p: 3, position: 'relative', overflow: 'hidden' }} >
                          <Box sx={{ width: 48, height: 48, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: itemBg, color: itemColor, mb: 2 }}>
                            <Icon icon={itemIcon} width={24} height={24} />
                          </Box>
                          <StyledTypography color="text.secondary" fontWeight="700" fontSize={isSmallScreen ? theme.spacing(1.4) : theme.spacing(1.6)} sx={{ textTransform: 'uppercase', letterSpacing: 1, mb: 0.5, background: 'transparent' }}>
                            {label}
                          </StyledTypography>
                          <StyledTypography color="text.primary" fontWeight="800" fontSize={isSmallScreen ? theme.spacing(2.2) : theme.spacing(3)} sx={{ background: 'transparent' }} >
                            {prefix} {value} {suffix}
                          </StyledTypography>
                        </StyledCard>
                      </Grid>
                    )})
                  }
                </Grid>

                <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid rgba(145, 158, 171, 0.12)', p: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" p={isSmallScreen ? 1.5 : 3}>
                    <Box>
                      <StyledTypography fontWeight="800" fontSize={isSmallScreen ? theme.spacing(1.8) : theme.spacing(2.4)}>Energy Chart</StyledTypography>
                      <StyledTypography color="text.secondary" fontSize={isSmallScreen ? theme.spacing(1.2) : theme.spacing(1.6)}>{currentDateTime}</StyledTypography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(25, 118, 210, 0.08)', px: 2, py: 1, borderRadius: '12px' }}>
                      <Icon icon={ChargingFill} color={APP_BLUE} width={isSmallScreen ? 20 : 28} height={isSmallScreen ? 20 : 28} />
                      <StyledTypography color={APP_BLUE} fontWeight="700" fontSize={isSmallScreen ? theme.spacing(1.6) : theme.spacing(2.4)}> 
                        {transactionDetails.dynamicChargePercent || 0}%
                      </StyledTypography>
                    </Box>
                  </Stack>


                  {(() => {
                      // Calculate max limits using the formula from transactionDetails
                      const amountPaid = Number(transactionDetails.totalAmountPaid) || 0;
                      const { maxKwh, maxMins } = calculateMaxLimits(amountPaid, transactionDetails);

                      const pricePerKw = Number(transactionDetails.pricePerKw) || Number(transactionDetails.priceperKW) || 0;
                      const pricePerMin = Number(transactionDetails.pricePerMin) || Number(transactionDetails.priceperMin) || 0;
                      // Time-based if no KW pricing, but Min pricing exists.
                      const isTimeBased = pricePerKw <= 0 && pricePerMin > 0;

                      // Fallback for Y-axis scale
                      const maxDataKwh = graphData.length > 0 ? Math.max(...graphData.map(d => d.kwh), 0) : 0;

                      // Build chart data: start from (starttime, 0)
                      const startTimestamp = transactionDetails.starttime
                        ? (() => {
                            let tStr = transactionDetails.starttime;
                            if (typeof tStr === 'string' && !tStr.includes('Z') && !tStr.includes('+')) {
                              tStr = tStr.replace(' ', 'T') + 'Z';
                            }
                            return new Date(tStr).getTime();
                          })()
                        : Date.now();
                      const chartData = graphData.length > 0
                        ? graphData
                        : [
                            { time: startTimestamp, kwh: 0 },
                            { time: startTimestamp + 60000, kwh: 0 },
                          ];

                      const yMax = Math.max(maxKwh > 0 && !isTimeBased ? maxKwh * 1.15 : 0, maxDataKwh * 1.2, 0.5);

                      let limitXTime = null;
                      if (maxMins > 0 && isTimeBased) {
                        limitXTime = startTimestamp + (maxMins * 60000);
                      }
                      
                      const xMax = limitXTime ? Math.max(Date.now(), limitXTime, startTimestamp + 60000) : Math.max(Date.now(), startTimestamp + 60000);

                      // Find last real data point coordinates for the pulse
                      const lastPoint = graphData.length > 0 ? graphData[graphData.length - 1] : null;

                      return (
                        <Box sx={{ width: '100%', height: 280, mt: 1, mb: 1 }}>
                          <ResponsiveContainer width="100%" height={280}>
                            <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
                              <defs>
                                <linearGradient id="energyGradient" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor={APP_BLUE} stopOpacity={0.35} />
                                  <stop offset="95%" stopColor={APP_BLUE} stopOpacity={0.02} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="rgba(145, 158, 171, 0.2)" vertical={false} />
                              <XAxis
                                dataKey="time"
                                type="number"
                                domain={[startTimestamp, xMax]}
                                tickCount={5}
                                tickFormatter={(ts) => {
                                  const d = new Date(ts);
                                  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                }}
                                tick={{ fontSize: 11, fill: theme.palette.text.secondary, fontFamily: theme.typography.fontFamily }}
                                axisLine={{ stroke: 'rgba(145, 158, 171, 0.3)' }}
                                tickLine={false}
                              />
                              <YAxis
                                domain={[0, yMax]}
                                tick={{ fontSize: 11, fill: theme.palette.text.secondary, fontFamily: theme.typography.fontFamily }}
                                axisLine={{ stroke: 'rgba(145, 158, 171, 0.3)' }}
                                tickLine={false}
                                tickFormatter={(val) => Number(val).toFixed(2)}
                                label={{ value: 'kWh', angle: -90, position: 'insideLeft', offset: 10, style: { fontSize: 12, fill: theme.palette.text.secondary, fontFamily: theme.typography.fontFamily, fontWeight: 600 } }}
                              />
                              <RechartsTooltip content={<EnergyTooltip />} />
                              {maxKwh > 0 && !isTimeBased && connectorStat?.status === "Charging" && (
                                <ReferenceLine
                                  y={maxKwh}
                                  stroke="#FF6B6B"
                                  strokeDasharray="6 4"
                                  strokeWidth={1.5}
                                  label={{ value: `Energy Limit: ${maxKwh} kWh`, position: 'insideTopRight', fill: '#FF6B6B', fontSize: 11, fontWeight: 600 }}
                                />
                              )}
                              {maxMins > 0 && isTimeBased && connectorStat?.status === "Charging" && (
                                <ReferenceLine
                                  x={limitXTime}
                                  stroke="#FF6B6B"
                                  strokeDasharray="6 4"
                                  strokeWidth={1.5}
                                  label={{ value: `Time Limit: ${maxMins} mins`, position: 'insideTopRight', fill: '#FF6B6B', fontSize: 11, fontWeight: 600 }}
                                />
                              )}
                              <Area
                                type="monotone"
                                dataKey="kwh"
                                stroke={APP_BLUE}
                                strokeWidth={2.5}
                                fill="url(#energyGradient)"
                                dot={(props) => {
                                  const { cx, cy, index } = props;
                                  const isLast = index === chartData.length - 1;
                                  if (!isLast || cx == null || cy == null) return null;
                                  return (
                                    <g key={`pulse-${index}`}>
                                      <circle cx={cx} cy={cy} r={5} fill={APP_BLUE} stroke="#fff" strokeWidth={2.5} />
                                      <circle cx={cx} cy={cy} r={5} fill="none" stroke={APP_BLUE} strokeWidth={2}>
                                        <animate attributeName="r" from="5" to="20" dur="2s" repeatCount="indefinite" />
                                        <animate attributeName="stroke-opacity" from="0.8" to="0" dur="2s" repeatCount="indefinite" />
                                      </circle>
                                      <circle cx={cx} cy={cy} r={5} fill="none" stroke={APP_BLUE} strokeWidth={1.5}>
                                        <animate attributeName="r" from="5" to="16" dur="2s" begin="0.5s" repeatCount="indefinite" />
                                        <animate attributeName="stroke-opacity" from="0.6" to="0" dur="2s" begin="0.5s" repeatCount="indefinite" />
                                      </circle>
                                    </g>
                                  );
                                }}
                                activeDot={{ r: 4, fill: '#919EAB', stroke: '#fff', strokeWidth: 1.5 }}
                                isAnimationActive={true}
                                animationDuration={800}
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        </Box>
                      );
                    })()}
                </Paper>

              </>
            }
          </Stack>
        </Paper>
        }     
      </Container>
      <MuiSnackBar
        open={messageOption.open}
        severity={messageOption.variant}
        message={messageOption.message}
        onCloseFunc={() => setMessageOption((messageOp) => ({ ...messageOp, open: false }))}
      />
    </Page>
  )
}

export default ChargerTransactionStats
